import React, {useCallback, useEffect, useRef, useState} from "react";
import {useMap} from "ahooks";
import {isFile, isFolder} from "./utils";
import {ContextMenu, Menu, MenuItem} from "@blueprintjs/core";
import { v4 } from 'uuid';

// const example = [
//   {
//     id: v4(),
//     isFolder: true,
//     fileType: 'folder',
//     label: 'exercises',
//     hasCaret: true,
//     isExpanded: true,
//     icon: 'folder-open',
//     className: 'folder',
//     childNodes: [
//       {
//         id: v4(),
//         isFolder: false,
//         fileType: 'py',
//         label: 'stack.py',
//         hasCaret: false,
//         icon: 'code',
//         code: 'print("stack.py")',
//         className: 'file',
//       },
//       {
//         id: v4(),
//         isFolder: false,
//         fileType: 'py',
//         label: 'url.py',
//         hasCaret: false,
//         icon: 'code',
//         code: '# url.py',
//         className: 'file',
//       }
//     ]
//   },
//   {
//     id: v4(),
//     isFolder: false,
//     fileType: 'py',
//     label: 'main.py',
//     hasCaret: false,
//     icon: 'code',
//     code: '# main.py',
//     className: 'file',
//   },
//   {
//     id: v4(),
//     isFolder: false,
//     fileType: 'py',
//     label: 'test.py',
//     hasCaret: false,
//     icon: 'code',
//     code: '# test.py',
//     className: 'file',
//   },
// ];

const NodeActions = {
  SELECT: 'select',
  DELETE: 'delete'
};
const useCodeEditorData = (defaultDirectory = [], directoryEditable = false) => {
  const [directory, setDirectory] = useState(defaultDirectory);
  const [openedFiles, { set: setOpenedFile, remove: removeOpenedFile }] = useMap([]);
  const [openedFileId, setOpenedFileId] = useState('-1');
  const contextNode = useRef(null);
  const filesTreeRef = useRef();

  // 遍历并选择指定节点
  const traversalNodesAndAction = useCallback((nodeList, node, action = NodeActions.SELECT) => {
    if (!nodeList || nodeList.length === 0) {
      return;
    }
    for (let i = 0, len = nodeList.length; i < len; i++) {
      const curNode = nodeList[i];
      // 选择某个元素
      if (action === NodeActions.SELECT) {
        if (curNode.isFolder) {
          traversalNodesAndAction(curNode.childNodes, node, action);
          continue;
        }
        if (curNode.id === node.id) {
          curNode.isSelected = true;
          if (!openedFiles.has(curNode.id)) {
            setOpenedFile(curNode.id, curNode);
          }
          setOpenedFileId(curNode.id);
        } else {
          curNode.isSelected = false;
        }
      }
      if (action === NodeActions.DELETE) {
        if (curNode.id === node.id) {
          return nodeList.filter(item => item.id !== curNode.id);
        }
        if (curNode.isFolder) {
          const childNodes = traversalNodesAndAction(curNode.childNodes, node, action);
          if (typeof childNodes !== 'undefined') {
            curNode.childNodes = childNodes;
          }
        }
      }
    }
  }, [openedFiles, setOpenedFile]);

  // 关闭编辑器已打开的文件
  const handleCloseFile = useCallback((id) => {
    let prevId = null;
    let nextId = null;
    let searched = false;
    if (openedFileId !== id) {
      removeOpenedFile(id);
      return;
    }
    for (let [key] of openedFiles) {
      if (key !== id) {
        if (!searched) {
          prevId = key;
        }
      } else {
        searched = true;
        continue;
      }
      if (searched && nextId === null) {
        nextId = key;
      }
    }
    setOpenedFileId(prevId || nextId);
    removeOpenedFile(id);
  }, [openedFileId, setOpenedFileId, openedFiles, removeOpenedFile]);

  // 新建文件
  const handleCreateNewFile = useCallback(() => {
    setTimeout(() => {
      const filename = window.prompt('请输入文件名');
      if (!filename) {
        return;
      }
      const fileType = filename.substring(filename.lastIndexOf('.') + 1);
      const newFileNode = {
        id: v4(),
        isFolder: false,
        fileType: fileType,
        label: filename,
        hasCaret: false,
        icon: fileType === 'py' ? 'code' : 'document',
        code: '',
        className: 'file',
      };
      if (contextNode.current !== null) {
        if (!contextNode.current.childNodes) {
          contextNode.current.childNodes = [];
        }
        for (let i = 0, len = contextNode.current.childNodes.length; i < len; i++) {
          if (filename.trim() === contextNode.current.childNodes[i].label.trim()) {
            window.alert('文件名已存在');
            return;
          }
        }
        contextNode.current.childNodes.push(newFileNode);
      } else {
        for (let i = 0, len = directory.length; i < len; i++) {
          if (filename.trim() === directory[i].label.trim()) {
            window.alert('文件名已存在');
            return;
          }
        }
        directory.push(newFileNode);
      }
      traversalNodesAndAction(directory, newFileNode);
      setDirectory([...directory]);
    }, 0);
  }, [directory, traversalNodesAndAction]);

  // 新建目录
  const handleCreateNewFolder = useCallback(() => {
    setTimeout(() => {
      const folderName = window.prompt('请输入目录名');
      if (!folderName) {
        return;
      }
      // 判断是否重复名字
      const folderNode = {
        id: v4(),
        isFolder: true,
        fileType: 'folder',
        label: folderName,
        hasCaret: true,
        isExpanded: true,
        icon: 'folder-open',
        className: 'folder',
      };
      if (contextNode.current) {
        if (!contextNode.current.childNodes) {
          contextNode.current.childNodes = [];
        }
        for (let i = 0, len = contextNode.current.childNodes.length; i < len; i++) {
          if (folderName.trim() === contextNode.current.childNodes[i].label.trim()) {
            window.alert('目录名重复');
            return;
          }
        }
        contextNode.current.childNodes.push(folderNode);
      } else {
        for (let i = 0, len = directory.length; i < len; i++) {
          if (folderName.trim() === directory[i].label.trim()) {
            window.alert('目录名重复');
            return;
          }
        }
        directory.push(folderNode);
      }
      setDirectory([...directory]);
    }, 0);
  }, [directory]);

  // 删除文件
  const handleDeleteFileOrFolder = useCallback(() => {
    if (!contextNode.current) {
      return;
    }
    setTimeout(() => {
      if (window.confirm(`确认删除：${contextNode.current.label}？`)) {
        if (contextNode.current.isFolder) {
          if (contextNode.current.childNodes && contextNode.current.childNodes.length > 0) {
            window.alert('目录不为空，请先删除子目录和文件');
            return;
          }
        }
        const list = traversalNodesAndAction(directory, contextNode.current, NodeActions.DELETE);
        if (typeof list !== 'undefined') {
          setDirectory([...list]);
        } else {
          setDirectory([...directory]);
        }
        handleCloseFile(contextNode.current.id);
      }
    }, 0);
  }, [directory, handleCloseFile, traversalNodesAndAction]);

  const handleNodeClick = (node) => {
    if (typeof node.isExpanded === 'boolean') {
      node.isExpanded = !node.isExpanded;
    } else {
      traversalNodesAndAction(directory, node);
    }
    setDirectory([...directory]);
  };

  // 自动保存代码
  const handleChangeCode = (fileId, code) => {
    const file = openedFiles.get(fileId);
    if (file) {
      file.code = code;
    }
  };

  // 记录右键菜单对应的节点
  const handleNodeContextMenu = (node) => {
    contextNode.current = node;
  };

  // 显示右键菜单
  useEffect(() => {
    console.log(directoryEditable);
    if (directoryEditable === false) {
      filesTreeRef.current.oncontextmenu = () => {};
      return;
    }
    filesTreeRef.current.oncontextmenu = (e) => {
      e.preventDefault();
      contextNode.current = null;
      let menu = null;
      if (isFile(e.target)) {
        menu = (
          <Menu>
            <MenuItem icon='trash' text="删除文件" onClick={handleDeleteFileOrFolder} />
          </Menu>
        );
      } else if (isFolder(e.target)) {
        menu = (
          <Menu>
            <MenuItem icon='code' text="新建文件" onClick={handleCreateNewFile} />
            <MenuItem icon='folder-close' text="新建目录" onClick={handleCreateNewFolder} />
            <MenuItem icon='trash' text="删除目录" onClick={handleDeleteFileOrFolder} />
          </Menu>
        );
      } else {
        menu = (
          <Menu>
            <MenuItem icon='folder-close' text="新建目录" onClick={handleCreateNewFolder} />
            <MenuItem icon='code' text="新建文件" onClick={handleCreateNewFile} />
          </Menu>
        );
      }
      if (menu === null) {
        return;
      }
      ContextMenu.show(menu, { left: e.clientX, top: e.clientY }, () => {}, true);
    }
  }, [handleCreateNewFile, handleCreateNewFolder, handleDeleteFileOrFolder, directoryEditable]);

  return { directory, openedFiles, handleNodeClick, openedFileId, setOpenedFileId, handleCloseFile, handleChangeCode, handleNodeContextMenu, filesTreeRef };
};

export default useCodeEditorData;
